import React, { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';

import { useSelector } from 'react-redux';
import { useAuthUser, useAuthTenant } from 'store/auth';

import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Card, CardContent, Divider, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { Container } from 'Components/Container';
import Paper from '@mui/material/Paper';

import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import CheckBoxOutlineBlankOutlinedIcon from '@mui/icons-material/CheckBoxOutlineBlankOutlined';
import IndeterminateCheckBoxOutlinedIcon from '@mui/icons-material/IndeterminateCheckBoxOutlined';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  editPolicyVersionStatus,
  getPolicyById,
  getTotalPoliciesAcceptsByPolicyIdAndPolicyVersionId,
  sendPolicyUserAcceptRequest,
  getPolicyComments,
  addPolicyComment,
  rejectPolicyVersionWithEmail,
  regenerateAttachmentDownloadUrl,
} from 'services/policies-service';
import { apiClient } from 'services/api-client';
import { formatDateTimeToDateTimeBR, formatDateToDateBRPolish } from 'utils/date_utils';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { ButtonForm } from 'Components/Form/styles';
import 'react-quill/dist/quill.snow.css';
import { Column, Row } from 'Components/Grid';
import { useReactToPrint } from 'react-to-print';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';
import { useTranslation } from 'react-i18next';

// Configurar o worker do PDF.js com a versão local
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

export const PolicyVersionShow = () => {
  const { t } = useTranslation(['policy']);
  const { id, idVersion } = useParams();
  const user = useSelector(useAuthUser);
  const tenant = useSelector(useAuthTenant);
  const componentRef = useRef();
  const combinedRef = useRef();
  const navigate = useNavigate();

  const SwalReact = withReactContent(Swal);
  const [refetch, setRefetch] = useState(0);
  
  // Estados para controlar o conteúdo combinado
  const [showCombinedContent, setShowCombinedContent] = useState(false);
  const [pdfPages, setPdfPages] = useState([]);
  
  // Estados para a funcionalidade de transcrição
  const [showTranscriptionModal, setShowTranscriptionModal] = useState(false);
  const [transcribedText, setTranscribedText] = useState('');

  // Estados para comentários
  const [comments, setComments] = useState([]);
  
  // Estado para armazenar a URL atual do anexo (compartilhado com AttachmentViewer)
  const [currentAttachmentUrl, setCurrentAttachmentUrl] = useState(null);

  const {
    isLoading,
    error,
    data: policy,
  } = useQuery(['getPolicyById', refetch], async () => {
    const resPolicy = await getPolicyById(id);

    if (resPolicy.status === 200) return resPolicy.data;

    return null;
  });

  const { data: policyAccepts } = useQuery(['getTotalPolicyAccepts', refetch, id, idVersion], async () => {
    const resTotalPolicyAccepts = await getTotalPoliciesAcceptsByPolicyIdAndPolicyVersionId(id, idVersion);

    if (resTotalPolicyAccepts.status === 200) return resTotalPolicyAccepts.data;

    return { total: 0 };
  });

  // Simular comentários de exemplo (até o backend ser implementado)
  const loadExampleComments = () => {
    const exampleComments = [
      {
        id: 1,
        content: "Esta política precisa de ajustes na seção de segurança",
        type: "comment",
        authorName: "João Silva",
        authorRole: "Revisor",
        createdAt: "2025-01-15T10:30:00.000Z"
      },
      {
        id: 2,
        content: "Documento rejeitado por não atender aos critérios de compliance",
        type: "rejection",
        authorName: "Maria Santos",
        authorRole: "Aprovador",
        createdAt: "2025-01-14T14:20:00.000Z"
      },
      {
        id: 3,
        content: "Excelente trabalho na documentação dos processos",
        type: "comment",
        authorName: "Pedro Costa",
        authorRole: "Usuário",
        createdAt: "2025-01-13T09:15:00.000Z"
      }
    ];
    setComments(exampleComments);
  };

  // Carregar comentários quando a política for carregada
  useQuery(['getPolicyComments', id, idVersion, refetch], async () => {
    try {
      const response = await getPolicyComments(id, idVersion);
      if (response.status === 200) {
        setComments(response.data || []);
        return response.data;
      }
    } catch (error) {
      console.log('Erro ao carregar comentários, usando dados de exemplo:', error);
      // Se houver erro na API, carregar comentários de exemplo
      loadExampleComments();
    }
    return [];
  });

  const mutation = useMutation(
    () => {
      return sendPolicyUserAcceptRequest(id, idVersion);
    },
    {
      onError: error => {
        // An error happened!
        SwalReact.fire({
          title: 'Erro!',
          text: error.response.data.message ?? error.message,
          icon: 'error',
        });
      },
      onSuccess: data => {
        if (data.status === 200) {
          setRefetch(refetch + 1);
        }
      },
    },
  );

  const getClassificationPolicy = policy => {
    return policy ? policy?.classification : '';
  };

  const getVersionPolicy = (policy, idVersion) => {
    return policy ? policy?.policyVersions[idVersion]?.version : '';
  };

  // Função para verificar se o usuário atual pode adicionar comentários
  const canAddComment = () => {
    if (!policy || !policy.policyVersions || !policy.policyVersions[parseInt(idVersion)] || !user) {
      return false;
    }

    const currentVersion = policy.policyVersions[parseInt(idVersion)];
    const currentUserId = user.userId;

    // Verificar se é autor
    if (currentVersion.author && (currentVersion.author._id === currentUserId || currentVersion.author.id === currentUserId)) {
      return true;
    }

    // Verificar se é revisor
    if (currentVersion.reviewer && (currentVersion.reviewer._id === currentUserId || currentVersion.reviewer.id === currentUserId)) {
      return true;
    }

    // Verificar se é aprovador
    if (currentVersion.approver && (currentVersion.approver._id === currentUserId || currentVersion.approver.id === currentUserId)) {
      return true;
    }

    // Verificar se é publicador
    if (currentVersion.publisher && (currentVersion.publisher._id === currentUserId || currentVersion.publisher.id === currentUserId)) {
      return true;
    }

    return false;
  };

  // Função para verificar se o usuário atual pode visualizar o histórico de aprovação
  const canViewApprovalHistory = () => {
    if (!policy || !policy.policyVersions || !policy.policyVersions[parseInt(idVersion)] || !user) {
      return false;
    }

    const currentVersion = policy.policyVersions[parseInt(idVersion)];
    const currentUserId = user.userId;

    // Verificar se é autor
    if (currentVersion.author && (currentVersion.author._id === currentUserId || currentVersion.author.id === currentUserId)) {
      return true;
    }

    // Verificar se é revisor
    if (currentVersion.reviewer && (currentVersion.reviewer._id === currentUserId || currentVersion.reviewer.id === currentUserId)) {
      return true;
    }

    // Verificar se é aprovador
    if (currentVersion.approver && (currentVersion.approver._id === currentUserId || currentVersion.approver.id === currentUserId)) {
      return true;
    }

    return false;
  };

  // Função para verificar se o usuário atual pode exportar PDF
  const canExportPDF = () => {
    if (!policy || !policy.policyVersions || !policy.policyVersions[parseInt(idVersion)] || !user) {
      return false;
    }

    const currentVersion = policy.policyVersions[parseInt(idVersion)];
    const currentUserId = user.userId;

    // Verificar se é revisor
    if (currentVersion.reviewer && (currentVersion.reviewer._id === currentUserId || currentVersion.reviewer.id === currentUserId)) {
      return true;
    }

    // Verificar se é aprovador
    if (currentVersion.approver && (currentVersion.approver._id === currentUserId || currentVersion.approver.id === currentUserId)) {
      return true;
    }

    // Verificar se é publicador
    if (currentVersion.publisher && (currentVersion.publisher._id === currentUserId || currentVersion.publisher.id === currentUserId)) {
      return true;
    }

    if (user.roleActive !== 'user_default') {
      return true;
    }

    return false;
  };

  const updateStatus = async status => {
    try {
      SwalReact.showLoading();
      await editPolicyVersionStatus(id, idVersion, status);
      SwalReact.hideLoading();
      SwalReact.fire({
        title: t('policy:versionShow.swal.successTitle'),
        text: t('policy:versionShow.swal.statusSuccess'),
        icon: 'success',
        willClose: () => {
          setRefetch(refetch + 1);
        },
      });
    } catch (error) {
      SwalReact.fire({
        title: t('policy:versionShow.swal.errorTitle'),
        text: error.response.data.message,
        icon: 'error',
      });
    }
  };

  const handleReject = async (tipo) => {
    // Mostrar modal para obter motivo da recusa
    const getTitleByType = (tipo) => {
      if (tipo === 'revisor') return t('policy:versionShow.rejectTypes.reviewer');
      if (tipo === 'aprovador') return t('policy:versionShow.rejectTypes.approver');
      if (tipo === 'publicador') return t('policy:versionShow.rejectTypes.publisher');
      return t('policy:versionShow.rejectTypes.document');
    };

    const { value: motivo } = await SwalReact.fire({
      title: t('policy:versionShow.swal.rejectTitle', { type: getTitleByType(tipo) }),
      html: `
        <div style="text-align: left; margin-bottom: 15px;">
          <p>${t('policy:versionShow.swal.rejectInfo')}</p>
          <p><strong>${t('policy:versionShow.swal.rejectReason')}</strong></p>
        </div>
      `,
      input: 'textarea',
      inputPlaceholder: t('policy:versionShow.swal.rejectPlaceholder'),
      inputAttributes: {
        'aria-label': t('policy:versionShow.swal.rejectAriaLabel'),
        style: 'min-height: 100px; resize: vertical;'
      },
      showCancelButton: true,
      confirmButtonText: t('policy:versionShow.swal.rejectConfirm'),
      cancelButtonText: t('policy:versionShow.swal.rejectCancel'),
      confirmButtonColor: '#d32f2f',
      cancelButtonColor: '#666',
      inputValidator: (value) => {
        if (!value || value.trim().length < 10) {
          return t('policy:versionShow.swal.rejectMinChars');
        }
      },
      customClass: {
        popup: 'reject-modal',
        confirmButton: 'reject-confirm-btn',
        input: 'reject-textarea'
      },
      didOpen: () => {
        // Adicionar estilos customizados para o modal
        const style = document.createElement('style');
        style.textContent = `
          .reject-modal {
            font-family: 'Source Sans Pro', sans-serif;
          }
          .reject-confirm-btn {
            background-color: #d32f2f !important;
            border-color: #d32f2f !important;
          }
          .reject-confirm-btn:hover {
            background-color: #b71c1c !important;
            border-color: #b71c1c !important;
          }
          .reject-textarea {
            font-family: 'Source Sans Pro', sans-serif;
            border: 2px solid #ddd !important;
            border-radius: 4px !important;
          }
          .reject-textarea:focus {
            border-color: #d32f2f !important;
            outline: none !important;
          }
        `;
        document.head.appendChild(style);
      }
    });

    if (motivo) {
      try {
        SwalReact.showLoading();
        
        let regressionStatus;
        if (tipo === 'revisor') {
          regressionStatus = 'Iniciado';
        } else if (tipo === 'aprovador') {
          regressionStatus = 'Iniciado';
        } else if (tipo === 'publicador') {
          regressionStatus = 'Aprovado';
        }
        
        // Preparar dados da rejeição
        const rejectionData = {
          policyId: id,
          policyVersionId: idVersion,
          content: `Documento rejeitado: ${motivo.trim()}`,
          motivo: motivo.trim(),
          type: 'rejection',
          authorId: user.userId,
          authorName: user.name || user.email || 'Usuário',
          authorRole: tipo === 'revisor' ? 'Revisor' : tipo === 'aprovador' ? 'Aprovador' : 'Publicador',
          createdAt: new Date().toISOString()
        };
        
        try {
          // Usar o novo endpoint que envia email automaticamente
          const rejectionPayload = {
            status: regressionStatus,
            rejectionReason: motivo.trim(),
            rejectedBy: user.name || user.email || 'Usuário',
            rejectedByType: tipo
          };
          
          await rejectPolicyVersionWithEmail(id, idVersion, rejectionPayload);
          
          // Adicionar rejeição à lista local de comentários para exibição imediata
          const rejectionComment = {
            id: Date.now(), // ID temporário
            ...rejectionData
          };
          setComments(prevComments => [rejectionComment, ...prevComments]);
          
        } catch (error) {
          console.error('Erro ao processar rejeição:', error);
          // Fallback para o método antigo se o novo falhar
          try {
            await editPolicyVersionStatus(id, idVersion, regressionStatus);
            await addPolicyComment(id, idVersion, rejectionData);
          } catch (fallbackError) {
            console.error('Erro no fallback:', fallbackError);
          }
          throw error;
        }
        
        SwalReact.hideLoading();
        SwalReact.fire({
          title: t('policy:versionShow.swal.rejectedTitle'),
          html: `
            <div style="text-align: left;">
              <p>${t('policy:versionShow.swal.rejectedText')}</p>
              <p><strong>${t('policy:versionShow.swal.rejectedReason')}</strong> ${motivo.trim()}</p>
              <p><em>${t('policy:versionShow.swal.rejectedEmail')}</em></p>
            </div>
          `,
          icon: 'info',
          confirmButtonText: 'OK',
          willClose: () => {
            setRefetch(refetch + 1);
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          },
        });
      } catch (error) {
        SwalReact.hideLoading();
        SwalReact.fire({
          title: t('policy:versionShow.swal.errorTitle'),
          text: error?.response?.data?.message || t('policy:versionShow.swal.rejectError'),
          icon: 'error',
        });
      }
    }
  };

  const handleAddComment = async () => {
    // Modal para adicionar comentário geral
    const { value: comentario } = await SwalReact.fire({
      title: t('policy:versionShow.swal.addCommentTitle'),
      html: `
        <div style="text-align: left; margin-bottom: 15px;">
          <p>${t('policy:versionShow.swal.addCommentInfo')}</p>
        </div>
      `,
      input: 'textarea',
      inputPlaceholder: t('policy:versionShow.swal.addCommentPlaceholder'),
      inputAttributes: {
        'aria-label': t('policy:versionShow.swal.addCommentAriaLabel'),
        style: 'min-height: 120px; resize: vertical;'
      },
      showCancelButton: true,
      confirmButtonText: t('policy:versionShow.swal.addCommentConfirm'),
      cancelButtonText: t('policy:versionShow.swal.addCommentCancel'),
      confirmButtonColor: '#1976d2',
      cancelButtonColor: '#666',
      inputValidator: (value) => {
        if (!value || value.trim().length < 5) {
          return t('policy:versionShow.swal.addCommentMinChars');
        }
      },
      customClass: {
        popup: 'comment-modal',
        confirmButton: 'comment-confirm-btn',
        input: 'comment-textarea'
      },
      didOpen: () => {
        // Adicionar estilos customizados para o modal
        const style = document.createElement('style');
        style.textContent = `
          .comment-modal {
            font-family: 'Source Sans Pro', sans-serif;
          }
          .comment-confirm-btn {
            background-color: #1976d2 !important;
            border-color: #1976d2 !important;
          }
          .comment-confirm-btn:hover {
            background-color: #1565c0 !important;
            border-color: #1565c0 !important;
          }
          .comment-textarea {
            font-family: 'Source Sans Pro', sans-serif;
            border: 2px solid #ddd !important;
            border-radius: 4px !important;
          }
          .comment-textarea:focus {
            border-color: #1976d2 !important;
            outline: none !important;
          }
        `;
        document.head.appendChild(style);
      }
    });

    if (comentario) {
      try {
        SwalReact.showLoading();
        
        // Determinar o papel do usuário na política
        let userRole = 'Usuário';
        if (policy && policy.policyVersions && policy.policyVersions[parseInt(idVersion)]) {
          const currentVersion = policy.policyVersions[parseInt(idVersion)];
          const userId = user.userId;
          
          if (currentVersion.author && (currentVersion.author._id === userId || currentVersion.author.id === userId)) {
            userRole = 'Autor';
          } else if (currentVersion.reviewer && (currentVersion.reviewer._id === userId || currentVersion.reviewer.id === userId)) {
            userRole = 'Revisor';
          } else if (currentVersion.approver && (currentVersion.approver._id === userId || currentVersion.approver.id === userId)) {
            userRole = 'Aprovador';
          }
        }
        
        // Preparar dados do comentário
        const commentData = {
          policyId: id,
          policyVersionId: idVersion,
          content: comentario.trim(),
          type: 'comment',
          authorId: user.userId,
          authorName: user.name || user.email || 'Usuário',
          authorRole: userRole,
          createdAt: new Date().toISOString()
        };
        
        try {
          // Tentar salvar no backend primeiro
          await addPolicyComment(id, idVersion, commentData);
          
          // Se sucesso, adicionar à lista local
          const newComment = {
            id: Date.now(), // ID temporário até recarregar
            ...commentData
          };
          setComments(prevComments => [newComment, ...prevComments]);
          
        } catch (apiError) {
          console.log('Erro na API de comentários, usando fallback:', apiError);
          
          // Fallback: usar API de status existente
          await editPolicyVersionStatus(id, idVersion, 'Comentario');
          
          // Adicionar comentário à lista local para exibição imediata
          const newComment = {
            id: Date.now(), // ID temporário
            ...commentData
          };
          setComments(prevComments => [newComment, ...prevComments]);
        }
        
        SwalReact.hideLoading();
        SwalReact.fire({
          title: t('policy:versionShow.swal.addCommentSuccess'),
          text: t('policy:versionShow.swal.addCommentSuccessText'),
          icon: 'success',
          confirmButtonText: 'OK',
          willClose: () => {
            setRefetch(refetch + 1);
          },
        });
      } catch (error) {
        SwalReact.hideLoading();
        SwalReact.fire({
          title: t('policy:versionShow.swal.errorTitle'),
          text: error?.response?.data?.message || t('policy:versionShow.swal.addCommentError'),
          icon: 'error',
        });
      }
    }
  };

  const getStatusReview = policyVersion => {
    if (policyVersion.status === 'Publicado') {
      return (
        <div style={{ verticalAlign: 'middle', display: 'flex' }}>
          <TaskAltOutlinedIcon sx={{ fill: 'green' }} />
          &nbsp;{t('policy:versionShow.status.published')}
        </div>
      );
    } else if (policyVersion.reviewer?._id !== user.userId) {
      return (
        <div style={{ verticalAlign: 'middle', display: 'flex' }}>
          <IndeterminateCheckBoxOutlinedIcon sx={{ '&:hover': { cursor: 'not-allowed' }, fill: 'red' }} />
          &nbsp;{t('policy:versionShow.status.notReviewer')}
        </div>
      );
    } else if (policyVersion.status === 'Iniciado') {
      return (
        <div style={{ verticalAlign: 'middle', display: 'flex' }}>
          <IndeterminateCheckBoxOutlinedIcon sx={{ '&:hover': { cursor: 'not-allowed' }, fill: 'red' }} />
          &nbsp;{t('policy:versionShow.status.waitingCompletion')}
        </div>
      );
    } else if (policyVersion.status === 'Concluído') {
      return (
        <div style={{ verticalAlign: 'middle', display: 'flex', gap: '10px', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <CheckBoxOutlineBlankOutlinedIcon
              sx={{ '&:hover': { cursor: 'pointer' }, fill: '#0039C5' }}
              onClick={() => updateStatus('Revisado')}
              title="Revisar política"
            />
            <span style={{ fontSize: '14px' }}>{t('policy:versionShow.status.approve')}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <CloseIcon
              sx={{ '&:hover': { cursor: 'pointer' }, fill: '#d32f2f' }}
              onClick={() => handleReject('revisor')}
              title="Recusar revisão"
            />
            <span style={{ fontSize: '14px' }}>{t('policy:versionShow.status.reject')}</span>
          </div>
        </div>
      )
    } else {
      return (
         <div style={{ verticalAlign: 'middle', display: 'flex' }}>
          <TaskAltOutlinedIcon sx={{ fill: 'green' }} />
          &nbsp;{t('policy:versionShow.status.reviewed')}
        </div>
      );
    }
  };

  const getStatusApprove = policyVersion => {
    if (policyVersion.status === 'Publicado') {
      return (
        <div style={{ verticalAlign: 'middle', display: 'flex' }}>
          <TaskAltOutlinedIcon sx={{ fill: 'green' }} />
          &nbsp;{t('policy:versionShow.status.published')}
        </div>
      );
    } else if (user.userId !== policyVersion.approver?._id) {
      return (
        <div style={{ verticalAlign: 'middle', display: 'flex' }}>
          <IndeterminateCheckBoxOutlinedIcon sx={{ '&:hover': { cursor: 'not-allowed' }, fill: 'red' }} />
          &nbsp;{t('policy:versionShow.status.notApprover')}
        </div>
      );
    } else if (policyVersion.status === 'Iniciado' || policyVersion.status === 'Concluído') {
      return (
        <div style={{ verticalAlign: 'middle', display: 'flex' }}>
          <IndeterminateCheckBoxOutlinedIcon sx={{ '&:hover': { cursor: 'not-allowed' }, fill: 'red' }} />
          &nbsp;{t('policy:versionShow.status.waitingReview')}
        </div>
      );
    } else if (policyVersion.status === 'Revisado') {
      return (
        <div style={{ verticalAlign: 'middle', display: 'flex', gap: '10px', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <CheckBoxOutlineBlankOutlinedIcon
              sx={{ '&:hover': { cursor: 'pointer' }, fill: '#0039C5' }}
              onClick={() => updateStatus('Aprovado')}
              title="Aprovar política"
            />
            <span style={{ fontSize: '14px' }}>{t('policy:versionShow.status.approve')}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <CloseIcon
              sx={{ '&:hover': { cursor: 'pointer' }, fill: '#d32f2f' }}
              onClick={() => handleReject('aprovador')}
              title="Recusar revisão"
            />
            <span style={{ fontSize: '14px' }}>{t('policy:versionShow.status.reject')}</span>
          </div>
        </div>
      )
    } else {
      return (
         <div style={{ verticalAlign: 'middle', display: 'flex' }}>
          <TaskAltOutlinedIcon sx={{ fill: 'green' }} />
          &nbsp;{t('policy:versionShow.status.approved')}
        </div>
      );
    }
  };

  const getStatusPublish = policyVersion => {
    if (policyVersion.status === 'Publicado' ) {
      return (
        <div style={{ verticalAlign: 'middle', display: 'flex' }}>
          <TaskAltOutlinedIcon sx={{ fill: 'green' }} />
          &nbsp;{t('policy:versionShow.status.published')}
        </div>
      );
    } else if (user.userId !== policyVersion.author?._id) {
      return (
        <div style={{ verticalAlign: 'middle', display: 'flex' }}>
          <IndeterminateCheckBoxOutlinedIcon sx={{ '&:hover': { cursor: 'not-allowed' }, fill: 'red' }} />
          &nbsp;{t('policy:versionShow.status.notPublisher')}
        </div>
      );
    } else if (policyVersion.status === 'Aprovado') {
      return (
        <div style={{ verticalAlign: 'middle', display: 'flex', gap: '10px', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <CheckBoxOutlineBlankOutlinedIcon
              sx={{ '&:hover': { cursor: 'pointer' }, fill: '#0039C5' }}
              onClick={() => updateStatus('Publicado')}
              title="Publicar"
            />
            <span style={{ fontSize: '14px' }}>{t('policy:versionShow.status.publish')}</span>
          </div>
        </div>
      );
    } else {
      return (
        <div style={{ verticalAlign: 'middle', display: 'flex' }}>
          <IndeterminateCheckBoxOutlinedIcon sx={{ '&:hover': { cursor: 'not-allowed' }, fill: 'red' }} />
          &nbsp;{t('policy:versionShow.status.waitingApproval')}
        </div>
      );
    }
  };

  // Função para imprimir com conteúdo do PDF anexado
  const handlePrintWithAttachment = async () => {
    const policyVersion = policy?.policyVersions[idVersion];
    
    if (policyVersion?.attachment && policyVersion.attachment.mimetype === 'application/pdf') {
      try {
        // Mostrar loading
        SwalReact.fire({
          title: t('policy:versionShow.swal.preparingPdf'),
          text: t('policy:versionShow.swal.extractingContent'),
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          willOpen: () => {
            SwalReact.showLoading();
          }
        });

        // Usar a URL atual ou a original do anexo
        const urlToUse = currentAttachmentUrl || policyVersion.attachment.url;
        const fullUrl = urlToUse.startsWith('http')
          ? urlToUse
          : `${apiClient.defaults.baseURL}${urlToUse}`;
        
        // Carregar e renderizar todas as páginas do PDF
        const response = await fetch(fullUrl);
        const arrayBuffer = await response.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        
        const PRINT_DPI = 150;                // qualidade melhor no papel
        const SCALE = PRINT_DPI / 72;         // 72dpi base do PDF
        const pages = [];
        
        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          const page = await pdf.getPage(pageNum);
          const viewport = page.getViewport({ scale: SCALE });
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          canvas.width = Math.ceil(viewport.width);
          canvas.height = Math.ceil(viewport.height);
          
          await page.render({
            canvasContext: context,
            viewport: viewport
          }).promise;
          
          pages.push(canvas.toDataURL('image/png'));
        }
        
        SwalReact.close();
        setPdfPages(pages);
        setShowCombinedContent(true);
        
        // imprime somente após o React pintar o combinedRef
        setTimeout(() => {
          printCombined();
          // limpeza leve após a impressão
          setTimeout(() => {
            setShowCombinedContent(false);
            setPdfPages([]);
          }, 1000);
        }, 300);
        
      } catch (e) {
        SwalReact.close();
        console.error('Erro ao processar PDF para impressão:', e);
        handlePrint(); // fallback
      }
    } else {
      // Se não houver anexo PDF, usar a impressão normal
      handlePrint();
    }
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `${policy?.name} - v${policy?.policyVersions?.[idVersion]?.version || ''}`,
    pageStyle: `
      @page { 
        size: A4; 
        margin: 0;
      }
      @media print {
        .no-print { display: none !important; }
        
        /* Estilo da capa */
        .document-cover {
          page-break-after: always;
          position: relative;
        }
        
        /* Evitar quebras no meio de elementos */
        img, table, tr, td, th, .ql-editor, .no-break {
          page-break-inside: avoid;
          break-inside: avoid;
        }
        
        /* Conteúdo após a capa com margens normais */
        .container > *:not(.document-cover) {
          margin-left: 20mm;
          margin-right: 20mm;
          margin-top: 15mm;
        }
        
        /* Primeiro elemento após a capa */
        .document-cover + * {
          margin-top: 20mm !important;
        }
      }
    `,
  });

  const printCombined = useReactToPrint({
    content: () => combinedRef.current,
    documentTitle: `${policy?.name} - v${policy?.policyVersions?.[idVersion]?.version || ''}`,
    pageStyle: `
      @page { size: A4; margin: 12mm; }
      @media print {
        html, body { -webkit-print-color-adjust: exact; }
        /* Evita quebras feias em imagens e tabelas */
        img, table, tr, td, th, .ql-editor, .no-break {
          page-break-inside: avoid;
          break-inside: avoid;
        }
        .page-break { page-break-before: always; break-before: page; }
        /* Tabelas mais legíveis no papel */
        table { border-collapse: collapse; width: 100%; }
        th, td { border: 1px solid #ddd; padding: 8px; }
        /* Conteúdo Quill com tipografia consistente no print */
        .ql-editor { font-size: 12pt; line-height: 1.5; }
        /* Imagens do PDF renderizado ocupam a largura da página */
        .pdf-page-img { width: 100%; height: auto; }
        /* Ocultar comentários na impressão */
        .no-print { display: none !important; }
      }
    `,
  });

  // Função para salvar o texto transcrito
  const handleSaveTranscription = () => {
    // Aqui você pode implementar a lógica para salvar o texto editado
    // Por exemplo, criar uma nova versão da política com o texto transcrito
    SwalReact.fire({
      title: t('policy:versionShow.swal.successTitle'),
      text: t('policy:versionShow.swal.transcriptionSaved'),
      icon: 'success',
      confirmButtonText: 'OK'
    }).then(() => {
      setShowTranscriptionModal(false);
      setTranscribedText('');
    });
  };

  // Função para determinar o ícone do arquivo baseado no tipo
  const getFileIcon = (mimetype) => {
    if (mimetype === 'application/pdf') {
      return <PictureAsPdfIcon sx={{ color: '#d32f2f', fontSize: 24 }} />;
    } else if (mimetype === 'application/msword' || mimetype === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      return <DescriptionIcon sx={{ color: '#1976d2', fontSize: 24 }} />;
    }
    return <AttachFileIcon sx={{ color: '#666', fontSize: 24 }} />;
  };

  // Função para formatar o tamanho do arquivo
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Componente para exibir o anexo
  const AttachmentViewer = React.memo(({ attachment }) => {
    const [attachmentUrl, setAttachmentUrl] = useState(null);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [iframeError, setIframeError] = useState(false);
    const iframeRef = React.useRef(null);

    // Inicializar com URL original
    React.useEffect(() => {
      const init = async () => {
        if (!attachment) return;

        console.log("🚀 Inicializando AttachmentViewer, pedindo URL sob demanda...");
        try {
          const response = await regenerateAttachmentDownloadUrl(id, idVersion);
          if (response.data?.url) {
            console.log("✅ URL fresca recebida:", response.data.url);
            setAttachmentUrl(response.data.url);
            setCurrentAttachmentUrl(response.data.url);
          } else {
            console.error("❌ Backend não retornou URL válida");
          }
        } catch (err) {
          console.error("❌ Erro ao gerar URL inicial:", err);
          // fallback para a que veio no objeto (se existir)
          setAttachmentUrl(attachment.url);
          setCurrentAttachmentUrl(attachment.url);
        }
      };

      init();
    }, [attachment]);

    // Monitorar mudanças na URL do attachment
    React.useEffect(() => {
      if (attachmentUrl) {
        console.log('🔄 URL do attachment alterada, iframe será re-renderizado:', attachmentUrl);
        console.log('🎯 Key do iframe:', attachmentUrl);
      }
    }, [attachmentUrl]);

    if (!attachment) return null;

    // Função para regenerar URL automaticamente (chamada pelo onError do iframe)
    const regenerateUrlAutomatically = async () => {
      try {
        setIsRegenerating(true);
        console.log('🔄 Iniciando regeneração automática da URL...');
        console.log('📋 Parâmetros:', { policyId: id, versionId: idVersion });
        console.log('🔗 URL atual:', attachmentUrl);
        
        const response = await regenerateAttachmentDownloadUrl(id, idVersion);
        console.log('📡 Resposta da API:', response);
        
        if (response.data?.url) {
          console.log('✅ URL regenerada com sucesso:', response.data.url);
          console.log('🔄 Atualizando estados...');
          setAttachmentUrl(response.data.url);
          setCurrentAttachmentUrl(response.data.url); // Manter compatibilidade
          setIframeError(false);
          console.log('✅ Estados atualizados, iframe deve recarregar automaticamente');
        } else {
          console.error('❌ Resposta da API não contém URL válida:', response.data);
          setIframeError(true);
        }
      } catch (error) {
        console.error('❌ Erro ao regenerar URL automaticamente:', error);
        console.error('📋 Detalhes do erro:', {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data
        });
        setIframeError(true);
      } finally {
        setIsRegenerating(false);
        console.log('🏁 Regeneração automática finalizada');
      }
    };

    // Função para regenerar URL manualmente
    const regenerateUrl = async () => {
      try {
        setIsRegenerating(true);
        const response = await regenerateAttachmentDownloadUrl(id, idVersion);
        if (response.data?.url) {
          setAttachmentUrl(response.data.url);
          setCurrentAttachmentUrl(response.data.url); // Manter compatibilidade
          setIframeError(false);
          SwalReact.fire({
            title: t('policy:versionShow.swal.successTitle'),
            text: response.data.message || t('policy:versionShow.swal.urlRegenSuccess'),
            icon: 'success',
          });
        }
      } catch (error) {
        console.error('Erro ao regenerar URL:', error);
        SwalReact.fire({
          title: t('policy:versionShow.swal.errorTitle'),
          text: t('policy:versionShow.swal.urlRegenError'),
          icon: 'error',
        });
      } finally {
        setIsRegenerating(false);
      }
    };

    // Função chamada quando o iframe carrega
    const handleIframeLoad = () => {
      console.log('Iframe carregado com sucesso para URL:', attachmentUrl);
      setIframeError(false);
      
      // Verificar se o conteúdo do iframe indica erro de acesso
      setTimeout(() => {
        try {
          const iframe = iframeRef.current;
          if (iframe && iframe.contentDocument) {
            const content = iframe.contentDocument.body?.innerText || '';
            console.log('Conteúdo do iframe:', content.substring(0, 200));
            
            // Verificar se contém mensagens de erro de acesso
            if (content.includes('Access Denied') || 
                content.includes('AccessDenied') || 
                content.includes('Forbidden') ||
                content.includes('403') ||
                content.includes('Token has expired') ||
                content.includes('SignatureDoesNotMatch') ||
                content.includes('ExpiredToken') ||
                content.includes('The provided token has expired')) {
              console.log('Erro de acesso detectado no conteúdo do iframe → regenerando URL');
              setIframeError(true);
              regenerateUrlAutomatically();
            }
          }
        } catch (error) {
          // CORS pode impedir acesso ao conteúdo, isso é normal
          console.log('Não foi possível verificar conteúdo do iframe (CORS):', error.message);
        }
      }, 1000); // Aguardar 1 segundo para o conteúdo carregar
    };

    // Função chamada quando o iframe falha ao carregar
    const handleIframeError = (event) => {
      console.log('Erro no iframe detectado:', event);
      console.log('URL que falhou:', attachmentUrl);
      setIframeError(true);
      regenerateUrlAutomatically();
    };

    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AttachFileIcon />
            {t('policy:versionShow.attachment.title')}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            {getFileIcon(attachment.mimetype)}
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                {attachment.originalName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {attachment.mimetype} • {formatFileSize(attachment.size)}
              </Typography>
            </Box>
          </Box>

          {/* Visualizador de PDF normal */}
          {attachment.mimetype === 'application/pdf' && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('policy:versionShow.attachment.pdfPreview')}
              </Typography>
              
              {/* Botão para regenerar URL se houver erro */}
              {iframeError && (
                <Box sx={{ mb: 2, p: 2, backgroundColor: '#fff3cd', borderRadius: 1, border: '1px solid #ffeaa7' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('policy:versionShow.attachment.linkExpired')}
                  </Typography>
                  <ButtonForm
                    secondary
                    size="small"
                    label={isRegenerating ? t('policy:versionShow.attachment.reloading') : t('policy:versionShow.attachment.reloadDocument')}
                    onClick={regenerateUrl}
                    disabled={isRegenerating}
                  />
                </Box>
              )}
              
              <Box 
                sx={{ 
                  border: '1px solid #ddd', 
                  borderRadius: 1,
                  overflow: 'hidden',
                  height: '600px',
                  width: '100%'
                }}
              >
                <iframe
                  ref={iframeRef}
                  key={attachmentUrl} // Força re-render quando URL muda
                  src={attachmentUrl || 'about:blank'}
                  width="100%"
                  height="100%"
                  style={{ border: 'none' }}
                  title={attachment.originalName}
                  onLoad={handleIframeLoad}
                  onError={handleIframeError}
                >
                  <p>
                    {t('policy:versionShow.attachment.iframeFallback')}
                    <a href={attachmentUrl || '#'} target="_blank" rel="noopener noreferrer">
                      {t('policy:versionShow.attachment.iframeFallbackLink')}
                    </a>
                  </p>
                </iframe>
              </Box>
            </Box>
          )}

          {/* Mensagem para outros tipos de arquivo */}
          {attachment.mimetype !== 'application/pdf' && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {t('policy:versionShow.attachment.noPreview')}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  });

  AttachmentViewer.displayName = 'AttachmentViewer';

  return (
    <>
      {!isLoading && !error && (
        <Container
          title={`${policy?.name} - Versão (${policy?.policyVersions[idVersion].version})`}
          breadcrumb={t('policy:versionShow.breadcrumb', { name: policy?.name })}
          linkPage={`/policies/show/${id}`}
          actionButton={
            policy?.policyVersions[idVersion]?.publishedDate && canExportPDF() ? (
              <ButtonForm 
                secondary 
                id="printPdf" 
                label={t('policy:versionShow.exportPdf')}
                onClick={() => handlePrintWithAttachment()}
              />
            ) : null
          }
        >
          <Box
            ref={componentRef}
            sx={{
              width: '100%',
              fontFamily:
                '"Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
            }}
            className="container"
          >
            {/* Capa do Documento - Aparece apenas na impressão */}
            <Box 
              className="document-cover"
              sx={{
                display: 'none',
                '@media print': {
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '297mm', // Altura A4
                  width: '210mm', // Largura A4
                  pageBreakAfter: 'always',
                  textAlign: 'center',
                  padding: '5px 40px 60px',
                  border: '3px solid #0039C5',
                  borderRadius: '0',
                  margin: '0',
                  position: 'relative',
                  boxSizing: 'border-box'
                }
              }}
            >
              {/* Logo da Empresa */}
              {tenant?.logo && (
                <Box sx={{ mb: 2, mt: 0 }}>
                  <img 
                    src={tenant.logo} 
                    alt={tenant.name || t('policy:versionShow.cover.companyLogo')}
                    style={{
                      maxWidth: '320px',
                      maxHeight: '180px',
                      objectFit: 'contain',
                      display: 'block'
                    }}
                  />
                </Box>
              )}
              
              {/* Nome da Empresa */}
              <Typography 
                sx={{ 
                  fontWeight: 600,
                  color: '#0039C5',
                  mb: 2,
                  fontSize: '24pt',
                  fontFamily: 'inherit'
                }}
              >
                {tenant?.name || t('policy:versionShow.cover.companyName')}
              </Typography>
              
              <Box sx={{ 
                width: '60%', 
                height: '3px', 
                backgroundColor: '#0039C5', 
                mb: 4, 
                mt: 1,
                '@media print': {
                  printColorAdjust: 'exact',
                  WebkitPrintColorAdjust: 'exact'
                }
              }} />
              
              {/* Título do Documento */}
              <Typography 
                sx={{ 
                  fontWeight: 700,
                  color: '#1a1a1a',
                  mb: 3,
                  fontSize: '28pt',
                  lineHeight: 1.3,
                  fontFamily: 'inherit',
                  px: 4
                }}
              >
                {policy.name}
              </Typography>
              
              {/* Classificação */}
              <Typography 
                sx={{ 
                  color: '#666',
                  mb: 4,
                  fontSize: '12pt',
                  textTransform: 'uppercase',
                  letterSpacing: '3px',
                  fontFamily: 'inherit',
                  fontWeight: 500
                }}
              >
                {getClassificationPolicy(policy)}
              </Typography>
              
              <Box sx={{ 
                width: '40%', 
                height: '1px', 
                backgroundColor: '#ccc', 
                mb: 4, 
                mt: 1,
                '@media print': {
                  printColorAdjust: 'exact',
                  WebkitPrintColorAdjust: 'exact'
                }
              }} />
              
              {/* Informações da Versão */}
              <Box sx={{ mb: 6 }}>
                <Typography sx={{ color: '#555', mb: 1.5, fontSize: '11pt', fontFamily: 'inherit' }}>
                  <strong>{t('policy:versionShow.versionLabel')}:</strong> {getVersionPolicy(policy, idVersion)}
                </Typography>
                <Typography sx={{ color: '#555', mb: 1.5, fontSize: '11pt', fontFamily: 'inherit' }}>
                  <strong>{t('policy:versionShow.publishedDateLabel')}:</strong> {policy?.policyVersions[idVersion]?.publishedDate ? formatDateToDateBRPolish(policy.policyVersions[idVersion].publishedDate) : t('policy:versionShow.notPublished')}
                </Typography>
                <Typography sx={{ color: '#555', fontSize: '11pt', fontFamily: 'inherit' }}>
                  <strong>{t('policy:versionShow.authorLabel')}:</strong> {policy?.policyVersions[idVersion]?.author?.name || 'N/A'}
                </Typography>
              </Box>
              
              {/* Rodapé */}
              <Box sx={{ 
                position: 'absolute', 
                bottom: '50px', 
                left: 0,
                right: 0,
                textAlign: 'center',
                px: 4
              }}>
                <Typography sx={{ 
                  color: '#999', 
                  fontSize: '9pt',
                  fontFamily: 'inherit',
                  mb: 1,
                  fontWeight: 500
                }}>
                  {t('policy:versionShow.officialDocument')}
                </Typography>
                <Typography sx={{
                  color: '#999',
                  fontSize: '8pt',
                  fontFamily: 'inherit'
                }}>
                  {tenant?.corporateName || tenant?.name || ''}
                </Typography>
              </Box>
            </Box>

            {/* Página 2: Título e Informações */}
            <Box sx={{
              '@media print': {
                pageBreakBefore: 'always',
                paddingTop: '20mm'
              }
            }}>
              <Row className="mb-1 pl-3">
                <Column xs={12}>
                  <h3 className="m-0">{policy.name}</h3>
                </Column>
                <Column xs={12}>
                  {t('policy:versionShow.versionLabel')}: {getVersionPolicy(policy, idVersion)} | {t('policy:versionShow.classificationLabel')}: {getClassificationPolicy(policy)}
                </Column>
                <Column xs={12}>
                  {t('policy:versionShow.publishedLabel')}: {policy?.policyVersions[idVersion]?.publishedDate ? formatDateToDateBRPolish(policy.policyVersions[idVersion].publishedDate) : t('policy:versionShow.notPublished')}
                </Column>
                <Column xs={12}>{t('policy:versionShow.authorLabel')}: {policy?.policyVersions[idVersion]?.author?.name || 'N/A'}</Column>
              </Row>
              
              {/* Informações de aceites */}
              {policy?.policyVersions[idVersion]?.publishedDate && policyAccepts?.total > 0 && (
                <Box sx={{ mb: 2, mt: 2 }}>
                  <Typography variant="body2" sx={{ color: '#555' }}>
                    <strong>{t('policy:versionShow.totalAccepts')}:</strong> {policyAccepts.total}
                  </Typography>
                </Box>
              )}
              
              <TableContainer
                component={Paper}
                style={{
                  marginBottom: '1rem',
                  marginTop: '1rem'
                }}
                sx={{
                  overflowX: 'visible',
                  '@media print': {
                    pageBreakAfter: 'always'
                  }
                }}
              >
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                  <TableRow key={'table-row-header'}>
                    <TableCell key={`thrc-stage`}>{t('policy:versionShow.table.stage')}</TableCell>
                    <TableCell key={`thrc-status`}>{t('policy:versionShow.table.status')}</TableCell>
                    <TableCell key={`thrc-date`}>{t('policy:versionShow.table.date')}</TableCell>
                    <TableCell key={`thrc-sponsor`}>{t('policy:versionShow.table.responsible')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow key={'table-row-header-review'}>
                    <TableCell key={`thrc-stage-1`}>{t('policy:versionShow.stage.review')}</TableCell>
                    <TableCell key={`thrc-status-1`}>{getStatusReview(policy?.policyVersions[idVersion])}</TableCell>
                    <TableCell key={`thrc-date-1`}>
                      {formatDateTimeToDateTimeBR(policy?.policyVersions[idVersion].reviewedDate)}
                    </TableCell>
                    <TableCell key={`thrc-sponsor-1`}>{policy?.policyVersions[idVersion]?.reviewer?.name || 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow key={'table-row-header-approve'}>
                    <TableCell key={`thrc-stage-2`}>{t('policy:versionShow.stage.approval')}</TableCell>
                    <TableCell key={`thrc-status-2`}>{getStatusApprove(policy?.policyVersions[idVersion])}</TableCell>
                    <TableCell key={`thrc-date-2`}>
                      {formatDateTimeToDateTimeBR(policy?.policyVersions[idVersion].approvedDate)}
                    </TableCell>
                    <TableCell key={`thrc-sponsor-2`}>{policy?.policyVersions[idVersion]?.approver?.name || 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow key={'table-row-header-publish'}>
                    <TableCell key={`thrc-stage-3`}>{t('policy:versionShow.stage.publication')}</TableCell>
                    <TableCell key={`thrc-status-3`}>{getStatusPublish(policy?.policyVersions[idVersion])}</TableCell>
                    <TableCell key={`thrc-date-3`}>
                      {formatDateTimeToDateTimeBR(policy?.policyVersions[idVersion].publishedDate)}
                    </TableCell>
                    <TableCell key={`thrc-sponsor-3`}>{policy?.policyVersions[idVersion]?.author?.name || 'N/A'}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            </Box>
            
            {/* Página 3: Conteúdo da Política */}
            <Box sx={{
              '@media print': {
                paddingTop: '20mm'
              }
            }}>
              {!policy?.policyVersions[idVersion]?.attachment && (
                <div>
                  <h3
                    style={{
                      textAlign: 'center',
                      fontSize: '1.75rem',
                      fontWeight: '500',
                      marginTop: '1.5rem',
                      marginBottom: '0.5rem',
                    }}
                  >
                    {policy.name}
                  </h3>
                </div>
              )}
              <div
                style={{ display: 'flex', justifyContent: 'center', flexDirection: 'column' }}
                className="ql-snow ql-editor"
                dangerouslySetInnerHTML={{ __html: policy?.policyVersions[idVersion].policyText }}
              ></div>
              
              {/* Exibir anexo se existir */}
              {policy?.policyVersions[idVersion]?.attachment && (
                <AttachmentViewer attachment={policy.policyVersions[idVersion].attachment} />
              )}
            </Box>
            
            {/* Histórico de Comentários */}
            {canViewApprovalHistory() && (
            <Accordion sx={{ mb: 2 }} className="no-print">
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="approval-history-content"
                id="approval-history-header"
                sx={{
                  backgroundColor: '#f5f5f5',
                  '&:hover': {
                    backgroundColor: '#e3f2fd'
                  }
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#1976d2' }}>
                    <IndeterminateCheckBoxOutlinedIcon />
                    {t('policy:versionShow.approvalHistory')} {policy?.policyVersions[idVersion]?.comments && policy.policyVersions[idVersion].comments.length > 0 && `(${policy.policyVersions[idVersion].comments.length})`}
                  </Typography>
                  {canAddComment() && (
                    <ButtonForm
                      secondary
                      label={t('policy:versionShow.addComment')}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddComment();
                      }}
                      size="small"
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                
                {comments && comments.length > 0 ? (
                  comments
                    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) // Ordenar por data (mais recente primeiro)
                    .map((comment, index) => (
                    <Box 
                      key={index} 
                      sx={{ 
                        mb: 2, 
                        p: 2, 
                        backgroundColor: comment.type === 'rejection' ? '#ffebee' : '#f0f8ff',
                        borderRadius: 1,
                        borderLeft: `4px solid ${comment.type === 'rejection' ? '#d32f2f' : '#1976d2'}`
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: comment.type === 'rejection' ? '#d32f2f' : '#1976d2' }}>
                          {comment.type === 'rejection' ? t('policy:versionShow.comment.rejected') : t('policy:versionShow.comment.comment')} {t('policy:versionShow.comment.by')} {comment.authorName || t('policy:versionShow.comment.user')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTimeToDateTimeBR(comment.createdAt)}
                        </Typography>
                      </Box>
                      
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>{t('policy:versionShow.comment.role')}:</strong> {(() => {
                          if (policy && policy.policyVersions && policy.policyVersions[parseInt(idVersion)]) {
                            const currentVersion = policy.policyVersions[parseInt(idVersion)];
                            const commentAuthorId = comment.authorId;

                            if (currentVersion.author && (currentVersion.author._id === commentAuthorId || currentVersion.author.id === commentAuthorId)) {
                              return t('policy:versionShow.comment.roleAuthor');
                            } else if (currentVersion.reviewer && (currentVersion.reviewer._id === commentAuthorId || currentVersion.reviewer.id === commentAuthorId)) {
                              return t('policy:versionShow.comment.roleReviewer');
                            } else if (currentVersion.approver && (currentVersion.approver._id === commentAuthorId || currentVersion.approver.id === commentAuthorId)) {
                              return t('policy:versionShow.comment.roleApprover');
                            }
                          }
                          return comment.authorRole || t('policy:versionShow.comment.user');
                        })()}
                      </Typography>
                      
                      <Typography variant="body1" sx={{ 
                        fontStyle: comment.type === 'rejection' ? 'normal' : 'italic',
                        backgroundColor: 'white',
                        p: 1.5,
                        borderRadius: 1,
                        border: '1px solid #e0e0e0'
                      }}>
                        &quot;{comment.content}&quot;
                      </Typography>
                      
                      {comment.type === 'rejection' && (
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" sx={{ 
                            backgroundColor: '#d32f2f', 
                            color: 'white', 
                            px: 1, 
                            py: 0.5, 
                            borderRadius: 1,
                            fontWeight: 'bold'
                          }}>
                            {t('policy:versionShow.comment.documentRejected')}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    {t('policy:versionShow.noComments')}
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
            )}
            
            {policy?.policyVersions[idVersion]?.publishedDate && policyAccepts?.total > 0 && (
              <div>
                <ButtonForm
                  label={t('policy:versionShow.viewAccepts')}
                  onClick={() => navigate(`/policies/show/${id}/version/${idVersion}/accepts`)}
                />
              </div>
            )}
            {policy?.policyVersions[idVersion]?.publishedDate && policyAccepts?.total === 0 && (
              <div>
                <ButtonForm label={t('policy:versionShow.sendAccepts')} onClick={() => mutation.mutate()} />
              </div>
            )}
          </Box>
        </Container>
      )}
      {isLoading && <div>{t('policy:versionShow.loading')}</div>}
      {error && <div>Erro</div>}
      
      {/* Componente invisível para impressão combinada */}
      {showCombinedContent && (
        <div style={{ display: 'none' }}>
          <div ref={combinedRef}>
            {/* Capa do Documento */}
            <div style={{ 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100vh',
              pageBreakAfter: 'always',
              textAlign: 'center',
              padding: '5px 40px 40px',
              border: '2px solid #0039C5',
              borderRadius: '8px',
              margin: '20px'
            }}>
              {/* Logo da Empresa */}
              {tenant?.logo && (
                <div style={{ marginBottom: '15px', marginTop: '0' }}>
                  <img 
                    src={tenant.logo} 
                    alt={tenant.name || t('policy:versionShow.cover.companyLogo')}
                    style={{
                      maxWidth: '320px',
                      maxHeight: '180px',
                      objectFit: 'contain'
                    }}
                  />
                </div>
              )}

              {/* Nome da Empresa */}
              <h1 style={{
                fontWeight: 600,
                color: '#0039C5',
                marginBottom: '10px',
                fontSize: '28pt'
              }}>
                {tenant?.name || t('policy:versionShow.cover.companyName')}
              </h1>
              
              <div style={{ width: '60%', height: '2px', backgroundColor: '#0039C5', marginBottom: '30px', marginTop: '10px' }} />
              
              {/* Título do Documento */}
              <h2 style={{ 
                fontWeight: 700,
                color: '#1a1a1a',
                marginBottom: '20px',
                fontSize: '32pt',
                lineHeight: 1.3
              }}>
                {policy?.name}
              </h2>
              
              {/* Classificação */}
              <h3 style={{ 
                color: '#666',
                marginBottom: '40px',
                fontSize: '14pt',
                textTransform: 'uppercase',
                letterSpacing: '2px'
              }}>
                {policy?.classification}
              </h3>
              
              <div style={{ width: '40%', height: '1px', backgroundColor: '#ccc', marginBottom: '40px', marginTop: '20px' }} />
              
              {/* Informações da Versão */}
              <div style={{ marginBottom: '40px' }}>
                <p style={{ color: '#555', marginBottom: '10px', fontSize: '12pt' }}>
                  <strong>{t('policy:versionShow.versionLabel')}:</strong> {policy?.policyVersions[idVersion]?.version}
                </p>
                <p style={{ color: '#555', marginBottom: '10px', fontSize: '12pt' }}>
                  <strong>{t('policy:versionShow.publishedDateLabel')}:</strong> {policy?.policyVersions[idVersion]?.publishedDate ? formatDateToDateBRPolish(policy.policyVersions[idVersion].publishedDate) : t('policy:versionShow.notPublished')}
                </p>
                <p style={{ color: '#555', fontSize: '12pt' }}>
                  <strong>{t('policy:versionShow.authorLabel')}:</strong> {policy?.policyVersions[idVersion]?.author?.name || 'N/A'}
                </p>
              </div>

              {/* Rodapé */}
              <div style={{ position: 'absolute', bottom: '40px', width: '100%', textAlign: 'center' }}>
                <p style={{ color: '#999', fontSize: '10pt', margin: 0 }}>
                  {t('policy:versionShow.officialDocument')}
                </p>
                <p style={{ color: '#999', fontSize: '9pt', marginTop: '10px', margin: 0 }}>
                  {tenant?.corporateName || tenant?.name || ''}
                </p>
              </div>
            </div>

            {/* Página 2: Cabeçalho e Tabela de Status */}
            <div style={{ pageBreakBefore: 'always', paddingTop: '20mm', marginBottom: '40px' }}>
              <h1 style={{ textAlign: 'center', marginBottom: '30px', fontSize: '24px', fontWeight: 'bold' }}>
                {policy?.name}
              </h1>
              <div style={{ marginBottom: '15px', fontSize: '14px' }}>
                <strong>{t('policy:versionShow.versionLabel')}:</strong> {policy?.policyVersions[idVersion]?.version} | <strong>{t('policy:versionShow.classificationLabel')}:</strong> {policy?.classification}
              </div>
              <div style={{ marginBottom: '15px', fontSize: '14px' }}>
                <strong>{t('policy:versionShow.publishedDateLabel')}:</strong> {policy?.policyVersions[idVersion]?.publishedDate ? formatDateToDateBRPolish(policy.policyVersions[idVersion].publishedDate) : t('policy:versionShow.notPublished')}
              </div>
              <div style={{ marginBottom: '30px', fontSize: '14px' }}>
                <strong>{t('policy:versionShow.authorLabel')}:</strong> {policy?.policyVersions[idVersion]?.author?.name || 'N/A'}
              </div>
              
              {/* Tabela de Status */}
              <div style={{ marginBottom: '40px', pageBreakAfter: 'always' }}>
                <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '20px', borderBottom: '2px solid #333', paddingBottom: '10px' }}>
                  {t('policy:versionShow.approvalStatus')}
                </h2>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    <th style={{ border: '1px solid #ddd', padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>
                      {t('policy:versionShow.table.stage')}
                    </th>
                    <th style={{ border: '1px solid #ddd', padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>
                      {t('policy:versionShow.table.status')}
                    </th>
                    <th style={{ border: '1px solid #ddd', padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>
                      {t('policy:versionShow.table.date')}
                    </th>
                    <th style={{ border: '1px solid #ddd', padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>
                      {t('policy:versionShow.table.responsible')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '12px' }}>{t('policy:versionShow.stage.review')}</td>
                    <td style={{ border: '1px solid #ddd', padding: '12px' }}>
                      {policy?.policyVersions[idVersion]?.reviewedDate ? t('policy:versionShow.statusReviewed') : t('policy:versionShow.statusPending')}
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '12px' }}>
                      {formatDateTimeToDateTimeBR(policy?.policyVersions[idVersion]?.reviewedDate) || 'N/A'}
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '12px' }}>
                      {policy?.policyVersions[idVersion]?.reviewer?.name || 'N/A'}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '12px' }}>{t('policy:versionShow.stage.approval')}</td>
                    <td style={{ border: '1px solid #ddd', padding: '12px' }}>
                      {policy?.policyVersions[idVersion]?.approvedDate ? t('policy:versionShow.statusApproved') : t('policy:versionShow.statusPending')}
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '12px' }}>
                      {formatDateTimeToDateTimeBR(policy?.policyVersions[idVersion]?.approvedDate) || 'N/A'}
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '12px' }}>
                      {policy?.policyVersions[idVersion]?.approver?.name || 'N/A'}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '12px' }}>{t('policy:versionShow.stage.publication')}</td>
                    <td style={{ border: '1px solid #ddd', padding: '12px' }}>
                      {policy?.policyVersions[idVersion]?.publishedDate ? t('policy:versionShow.statusPublished') : t('policy:versionShow.statusPending')}
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '12px' }}>
                      {formatDateTimeToDateTimeBR(policy?.policyVersions[idVersion]?.publishedDate) || 'N/A'}
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '12px' }}>
                      {policy?.policyVersions[idVersion]?.author?.name || 'N/A'}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            </div>
            
            {/* Página 3: Conteúdo da política */}
            <div style={{ paddingTop: '20mm', lineHeight: '1.6', marginBottom: '40px' }}>
              <div dangerouslySetInnerHTML={{ __html: policy?.policyVersions[idVersion]?.policyText || '' }} />
            </div>
            
            <div className="page-break" />
            
            {pdfPages.length > 0 && (
              <div style={{ marginTop: '20px', padding: '10px 0' }}>
                {pdfPages.map((src, i) => (
                  <div key={i} style={{ pageBreakBefore: i > 0 ? 'always' : 'auto', marginBottom: '10px' }}>
                    <img className="pdf-page-img" src={src} alt={`Página ${i + 1} do anexo`} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Modal de Transcrição */}
      <Dialog 
        open={showTranscriptionModal} 
        onClose={() => setShowTranscriptionModal(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {t('policy:versionShow.transcription.title')} - {policy?.policyVersions[idVersion]?.attachment?.originalName}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {t('policy:versionShow.transcription.description')}
          </Typography>
          <TextField
            multiline
            rows={20}
            fullWidth
            variant="outlined"
            value={transcribedText}
            onChange={(e) => setTranscribedText(e.target.value)}
            placeholder={t('policy:versionShow.transcription.placeholder')}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontFamily: 'monospace',
                fontSize: '14px',
                lineHeight: 1.6
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <ButtonForm
            secondary
            label={t('policy:versionShow.transcription.cancel')}
            onClick={() => setShowTranscriptionModal(false)}
          />
          <ButtonForm
            primary
            label={t('policy:versionShow.transcription.save')}
            onClick={handleSaveTranscription}
            disabled={!transcribedText.trim()}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};
